/**
 * Admin → Usuários.
 *
 * - admin: gerencia users do próprio tenant (cria operator/viewer).
 * - super_admin: gerencia qualquer user, qualquer tenant. Pode criar
 *   admin de tenant e outros super_admins.
 *
 * Inclui um card "minha conta" no topo pra trocar a própria senha.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Mail, Pencil, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { authApi } from "@/api/auth";
import { usersApi } from "@/api/users";
import { apiErrorMessage } from "@/api/client";
import { useTenants, useUsers } from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { Role, User, UserCreate, UserUpdate } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Input, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { Card, CardHeader } from "@/components/Card";
import { formatDate, shortId, timeAgo } from "@/lib/format";

const ROLE_LABEL: Record<Role, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  operator: "Operador",
  viewer: "Viewer",
};

const ROLE_TONE: Record<Role, "warning" | "brand" | "success" | "default"> = {
  super_admin: "warning",
  admin: "brand",
  operator: "success",
  viewer: "default",
};

export function AdminUsersPage() {
  const auth = useAuth();
  const users = useUsers();
  const tenants = useTenants(auth.isSuperAdmin);
  const qc = useQueryClient();

  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [showPwdDialog, setShowPwdDialog] = useState(false);

  const tenantNameById = useMemo(
    () => new Map((tenants.data ?? []).map((t) => [t.id, t.name])),
    [tenants.data],
  );

  const createMut = useMutation({
    mutationFn: (p: UserCreate) => usersApi.create(p),
    onSuccess: (u) => {
      toast.success(
        u.is_active
          ? `Usuário criado — boas-vindas enviadas para ${u.email}${u.whatsapp ? ` e WhatsApp ${u.whatsapp}` : ""}`
          : "Usuário criado (inativo: o convite sai quando você reenviar)",
      );
      void qc.invalidateQueries({ queryKey: ["users"] });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const conviteMut = useMutation({
    mutationFn: (id: string) => usersApi.reenviarConvite(id),
    onSuccess: (r) => toast.success(r.detail),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: UserUpdate }) =>
      usersApi.update(id, p),
    onSuccess: () => {
      toast.success("Usuário atualizado");
      void qc.invalidateQueries({ queryKey: ["users"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      toast.success("Usuário deletado");
      void qc.invalidateQueries({ queryKey: ["users"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<User>[] = [
    { key: "name", header: "Nome", cell: (r) => <span className="font-medium text-slate-900 dark:text-slate-100">{r.name ?? r.email.split("@")[0]}</span> },
    { key: "email", header: "Email", cell: (r) => <span className="text-slate-600 dark:text-slate-400">{r.email}</span> },
    { key: "role", header: "Papel", cell: (r) => <Badge tone={ROLE_TONE[r.role]}>{ROLE_LABEL[r.role]}</Badge> },
    {
      key: "tenant",
      header: "Tenant",
      cell: (r) =>
        r.tenant_id ? (
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {tenantNameById.get(r.tenant_id) ?? shortId(r.tenant_id)}
          </span>
        ) : (
          <span className="text-xs text-amber-700">staff envpulse</span>
        ),
      hideOnMobile: !auth.isSuperAdmin,
    },
    {
      key: "is_active",
      header: "Status",
      cell: (r) =>
        r.is_active ? (
          <Badge tone="success">ativo</Badge>
        ) : (
          <Badge tone="default">inativo</Badge>
        ),
      hideOnMobile: true,
    },
    {
      key: "last_seen_at",
      header: "Último acesso",
      cell: (r) => <UltimoAcesso user={r} />,
    },
    {
      key: "created_at",
      header: "Criado em",
      cell: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(r.created_at)}</span>,
      hideOnMobile: true,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          {r.is_active && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Mail className="h-4 w-4" />}
              loading={conviteMut.isPending && conviteMut.variables === r.id}
              onClick={() => conviteMut.mutate(r.id)}
              title="Manda de novo o e-mail de boas-vindas, com um link novo para criar a senha"
            >
              Reenviar convite
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Editar
          </Button>
          {r.id !== auth.user?.id && (
            <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Usuários"
        description="Quem tem acesso ao portal e em qual nível."
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
            Novo usuário
          </Button>
        }
      />

      {/* Self */}
      <Card>
        <CardHeader
          title="Minha conta"
          subtitle={auth.user?.email}
          actions={
            <Button
              size="sm"
              variant="secondary"
              icon={<KeyRound className="h-4 w-4" />}
              onClick={() => setShowPwdDialog(true)}
            >
              Trocar senha
            </Button>
          }
        />
      </Card>

      <DataTable
        data={users.data}
        columns={columns}
        loading={users.isLoading}
        rowKey={(r) => r.id}
        emptyTitle="Nenhum usuário cadastrado"
      />

      <UserFormModal
        key={isCreating ? "create" : `edit-${editTarget?.id ?? "none"}`}
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        title={isCreating ? "Novo usuário" : "Editar usuário"}
        initial={editTarget}
        isSuperAdminUI={auth.isSuperAdmin}
        tenantOptions={(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(payload, isEdit) => {
          if (isEdit && editTarget) {
            const upd: UserUpdate = {
              email: payload.email,
              name: payload.name,
              role: payload.role,
              is_active: payload.is_active,
              tenant_id: payload.tenant_id,
              whatsapp: payload.whatsapp,
            };
            if (payload.password) upd.password = payload.password;
            updateMut.mutate({ id: editTarget.id, p: upd });
          } else {
            // Senha em branco = o usuário cria a própria pelo convite.
            const { password, ...resto } = payload;
            createMut.mutate(password ? payload : resto);
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar usuário?"
        message={
          deleteTarget
            ? `Vai apagar '${deleteTarget.email}'. Você pode preferir desativar (toggle 'is_active') em vez de deletar — o histórico fica preservado.`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />

      <PasswordDialog open={showPwdDialog} onClose={() => setShowPwdDialog(false)} />
    </div>
  );
}

// ---------- último acesso ---------------------------------------------------

/**
 * "Logado" não existe: o token é sem estado e o servidor não sabe se a aba
 * está aberta. O que se sabe é a última requisição, gravada no máximo a cada
 * 2 min (api/app/atividade.py). Esta janela tem de ser MAIOR que isso, senão
 * quem está usando o app pisca entre online e offline.
 */
const ONLINE_MIN = 5;

function UltimoAcesso({ user }: { user: User }) {
  if (!user.last_seen_at) {
    // Sem acesso nenhum: convite ainda não usado (ou cadastro anterior à v44).
    return <span className="text-xs text-slate-400 dark:text-slate-500">nunca entrou</span>;
  }
  const online = Date.now() - new Date(user.last_seen_at).getTime() < ONLINE_MIN * 60_000;
  return (
    <div className="leading-tight">
      {online ? (
        <Badge tone="success">online agora</Badge>
      ) : (
        <span className="text-xs text-slate-600 dark:text-slate-300">{timeAgo(user.last_seen_at)}</span>
      )}
      {user.last_login_at && (
        <div className="text-[11px] text-slate-400 dark:text-slate-500">
          login {formatDate(user.last_login_at, "dd/MM HH:mm")}
        </div>
      )}
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

interface UserFormPayload extends UserCreate {
  password: string;
}

function UserFormModal({
  open,
  onClose,
  title,
  initial,
  isSuperAdminUI,
  tenantOptions,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: User | null;
  isSuperAdminUI: boolean;
  tenantOptions: { value: string; label: string }[];
  submitting?: boolean;
  onSubmit: (p: UserFormPayload, isEdit: boolean) => void;
}) {
  const [email, setEmail] = useState(initial?.email ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>(initial?.role ?? "viewer");
  const [tenantId, setTenantId] = useState(initial?.tenant_id ?? tenantOptions[0]?.value ?? "");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [whatsapp, setWhatsapp] = useState(initial?.whatsapp ?? "");

  useEffect(() => {
    setEmail(initial?.email ?? "");
    setName(initial?.name ?? "");
    setPassword("");
    setRole(initial?.role ?? "viewer");
    setTenantId(initial?.tenant_id ?? tenantOptions[0]?.value ?? "");
    setIsActive(initial?.is_active ?? true);
    setWhatsapp(initial?.whatsapp ?? "");
  }, [initial, tenantOptions]);

  const isEdit = !!initial;

  // Roles disponíveis variam pelo perfil de quem está cadastrando.
  const roleOptions = isSuperAdminUI
    ? [
        { value: "super_admin", label: "Super Admin (staff)" },
        { value: "admin",       label: "Admin (do tenant)" },
        { value: "operator",    label: "Operador" },
        { value: "viewer",      label: "Viewer" },
      ]
    : [
        { value: "operator", label: "Operador" },
        { value: "viewer",   label: "Viewer" },
      ];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: UserFormPayload = {
      email,
      name: name || null,
      password,
      role,
      is_active: isActive,
      // Vazio vira null: na edição, apagar o campo apaga o número.
      whatsapp: whatsapp.trim() || null,
      tenant_id: role === "super_admin" ? null : isSuperAdminUI ? tenantId : undefined,
    };
    onSubmit(payload, isEdit);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="user-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Maria Silva" />
          </Field>
          <Field label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="maria@cliente.com.br"
            />
          </Field>
        </div>

        <Field
          label="WhatsApp (opcional)"
          hint={
            isEdit
              ? undefined
              : "Preenchido, as boas-vindas saem também por WhatsApp, avisando que o link de senha está no e-mail. Não cadastra o número para receber alertas — isso é em Alertas › Canais."
          }
        >
          <Input
            type="tel"
            inputMode="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+5511999998888"
            autoComplete="tel"
          />
        </Field>

        <Field
          label={isEdit ? "Nova senha (deixa em branco pra manter)" : "Senha inicial (opcional)"}
          hint={
            isEdit
              ? undefined
              : "Em branco, o usuário cria a própria senha pelo e-mail de boas-vindas — que sai de qualquer forma, com as orientações de acesso."
          }
        >
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={6}
            placeholder={isEdit ? "••••••••" : "deixe em branco"}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Papel" required>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              required
              options={roleOptions}
            />
          </Field>
          {isSuperAdminUI && role !== "super_admin" && (
            <Field label="Tenant" required>
              <Select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
                options={tenantOptions}
                placeholder="Selecione o tenant"
              />
            </Field>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600 focus:ring-brand-500"
          />
          Conta ativa (pode fazer login)
        </label>
      </form>
    </Modal>
  );
}

// ---------- password modal --------------------------------------------------

function PasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirmPwd("");
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirmPwd) {
      toast.error("As senhas não conferem");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(current, next);
      toast.success("Senha trocada");
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Trocar minha senha"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="pwd-form" loading={submitting}>
            Trocar
          </Button>
        </>
      }
    >
      <form id="pwd-form" onSubmit={handleSubmit} className="space-y-4">
        <Field label="Senha atual" required>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </Field>
        <Field label="Nova senha" required>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Confirme a nova senha" required>
          <Input
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            required
            autoComplete="new-password"
          />
        </Field>
      </form>
    </Modal>
  );
}
